import { useCallback } from "react";
import { type SignedInUser } from "@platform/user";
import { useSignedInUser } from "@platform/user/client";

import { submitEvent } from "../utils/tracking";
import { useAnalyticsData } from "../contexts/analyticsContext";

const analyticsUserName = (user: SignedInUser): string =>
  [user.givenName ?? "", user.familyName ?? ""].join(" ").trim() ||
  user.displayName;

export const useAnalyticsEvents = (): {
  trackRfpStatusChange: (properties: object) => void;
  trackCarrierTrackerMarketingPresentation: (properties: object) => void;
  trackCarrierTrackerQuoteModal: (properties: object) => void;
  trackCarrierTrackerRequoteNeeded: (properties: object) => void;
  trackCarrierIncludedInMarketingPresentation: (properties: object) => void;
  trackCarrierTrackerCoverageStatus: (properties: object) => void;
  trackCarrierTrackerQuoteSubmitted: (properties: object) => void;
  trackRfpTemplateUpload: (properties: object) => void;
  trackRfpSubmission: (properties: object) => void;
  trackRfpTemplateGeneration: () => void;
  trackCensusUpload: () => void;
  trackEvent: (eventName: string, properties?: object) => void;
} => {
  const user = useSignedInUser();
  const { state: analyticsState, dispatch: analyticsDispatch } =
    useAnalyticsData();
  const companyProperties = {
    companyName: analyticsState.companyName,
    brokerName: analyticsState.brokerName,
  };

  const trackEventWithUser = useCallback(
    (eventName: string, properties?: object) => {
      submitEvent(eventName, {
        ...properties,
        userEmail: user.email,
        userName: analyticsUserName(user),
      });
    },
    [user],
  );

  const trackEventFactory =
    (eventName: (typeof TRACK_EVENTS)[keyof typeof TRACK_EVENTS]) =>
    (properties?: object) => {
      trackEventWithUser(eventName, properties);
    };

  const trackRfpStatusChange = (properties: object): void => {
    const initialProperties = {
      coverages: [
        ...analyticsState.coverageCurrentNames,
        ...analyticsState.coverageNewNames,
      ],
      connectedCarriers: {
        names: analyticsState.carrierConnectedNames,
        count: analyticsState.carrierConnectedCount,
      },
      unconnectedCarriers: {
        names: analyticsState.carrierUnconnectedNames,
        count: analyticsState.carrierUnconnectedCount,
      },
    };
    trackEventFactory(TRACK_EVENTS.RFP_STATUS_CHANGE)({
      ...initialProperties,
      ...companyProperties,
      ...properties,
    });
  };

  const trackCarrierTrackerMarketingPresentation = (
    properties: object,
  ): void => {
    trackEventFactory(TRACK_EVENTS.CARRIER_TRACKER_MARKETING_PRESENTATION)({
      ...companyProperties,
      ...properties,
    });
  };

  const trackCarrierTrackerQuoteModal = (
    properties:
      | {
          carrierName: string;
          coverage: string;
          requoteReason: string;
          status: "open";
        }
      | { status: "closed" },
  ): void => {
    if (properties.status === "open") {
      analyticsDispatch({
        quoteCarrierName: properties.carrierName,
        quoteProductType: properties.coverage,
        quoteRequoteReason: properties.requoteReason,
      });
    }
    trackEventFactory(TRACK_EVENTS.CARRIER_TRACKER_QUOTE_MODAL)({
      ...companyProperties,
      ...(properties.status === "closed"
        ? {
            carrierName: analyticsState.quoteCarrierName,
            coverage: analyticsState.quoteProductType,
            requoteReason: analyticsState.quoteRequoteReason,
          }
        : {}),
      ...properties,
    });
  };

  const trackCarrierTrackerRequoteNeeded = (properties: object): void => {
    trackEventFactory(TRACK_EVENTS.CARRIER_TRACKER_REQUOTE_NEEDED)({
      ...companyProperties,
      carrierName: analyticsState.quoteCarrierName,
      coverage: analyticsState.quoteProductType,
      ...properties,
    });
  };

  const trackCarrierIncludedInMarketingPresentation = (
    properties: object,
  ): void => {
    trackEventFactory(TRACK_EVENTS.CARRIER_INCLUDED_IN_MARKETING_PRESENTATION)({
      ...companyProperties,
      carrierName: analyticsState.quoteCarrierName,
      ...properties,
    });
  };

  const trackCarrierTrackerCoverageStatus = (properties: object): void => {
    trackEventFactory(TRACK_EVENTS.CARRIER_TRACKER_COVERAGE_STATUS)({
      ...companyProperties,
      carrierName: analyticsState.quoteCarrierName,
      coverage: analyticsState.quoteProductType,
      ...properties,
    });
  };

  const trackCarrierTrackerQuoteSubmitted = (properties: object): void => {
    trackEventFactory(TRACK_EVENTS.CARRIER_TRACKER_QUOTE_SUBMITTED)({
      ...companyProperties,
      coverage: analyticsState.quoteProductType,
      ...properties,
    });
  };

  const trackRfpTemplateUpload = (properties: object): void => {
    trackEventFactory(TRACK_EVENTS.RFP_TEMPLATE_UPLOAD)({
      ...companyProperties,
      ...properties,
    });
  };

  const trackRfpSubmission = (properties: object): void => {
    trackEventFactory(TRACK_EVENTS.RFP_SUBMISSION)({
      ...companyProperties,
      ...properties,
    });
  };

  const trackRfpTemplateGeneration = (): void => {
    trackEventFactory(TRACK_EVENTS.RFP_TEMPLATE_GENERATION)(companyProperties);
  };

  const trackCensusUpload = (): void => {
    trackEventFactory(TRACK_EVENTS.CENSUS_UPLOAD)({
      ...companyProperties,
      rfpId: analyticsState.rfpId,
    });
  };

  return {
    trackRfpStatusChange,
    trackCarrierTrackerMarketingPresentation,
    trackCarrierTrackerQuoteModal,
    trackCarrierTrackerRequoteNeeded,
    trackCarrierIncludedInMarketingPresentation,
    trackCarrierTrackerCoverageStatus,
    trackCarrierTrackerQuoteSubmitted,
    trackRfpTemplateUpload,
    trackRfpSubmission,
    trackRfpTemplateGeneration,
    trackCensusUpload,
    trackEvent: trackEventWithUser,
  };
};

const TRACK_EVENTS = {
  RFP_TEMPLATE_UPLOAD: "rfp_template_upload",
  RFP_SUBMISSION: "rfp_submission",
  RFP_TEMPLATE_GENERATION: "rfp_template_generation",
  CENSUS_UPLOAD: "census-upload",
  CARRIER_TRACKER_MARKETING_PRESENTATION:
    "carrier_tracker_marketing_presentation",
  CARRIER_TRACKER_QUOTE_SUBMITTED: "carrier_tracker_quote_submitted",
  CARRIER_TRACKER_QUOTE_MODAL: "carrier_tracker_quote_modal",
  CARRIER_TRACKER_REQUOTE_NEEDED: "carrier_tracker_requote_needed",
  RFP_STATUS_CHANGE: "rfp_status_change",
  CARRIER_INCLUDED_IN_MARKETING_PRESENTATION:
    "carrier_included_in_marketing_presentation",
  CARRIER_TRACKER_COVERAGE_STATUS: "carrier_tracker_coverage_status",
} as const;
